import { ApiService } from '../apiService.service';
import { Enrollment } from '@app/models/enrollment.model';
import { EnrollmentAdapter } from '@app/adapters/enrollment.adapter';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { firstValueFrom } from 'rxjs';
import { User } from '@app/models/_index';

@Injectable({
  providedIn: 'root',
})
export class EnrollmentService {
  enrollments: Enrollment[];

  constructor(
    private enrollmentAdapter: EnrollmentAdapter,
    private apiService: ApiService,
    private http: HttpClient
  ) {}

  /**
   * TODO: Enable this in the future.
   * @param errorRes - Error response if service false
   * @returns an Observable never
   */
  // private handleError(errorRes: HttpErrorResponse): Observable<never> {
  //   const errorMessage = 'An unknown error occurred!';
  //   if (!errorRes.error || !errorRes.error.error) {
  //     return throwError(errorMessage);
  //   }
  // }

  /**
   * Return enrollments by a given docebo user id.
   * Assert that the JWT authorization token bearer is active and included, else
   * you will get an unauthorized exception
   * @param userId - valid docebo user id
   * @returns - a Promise containing a collection of Enrollments
   */
  public getEnrollments(userId: number): Promise<Enrollment[]> {
    const options = {
      params: {
        userId: userId,
        // get a large response to allow us to filter out the possibility
        // of an overacheiver with a ton of 'complete' courses that need filtering
        pageSize: 200,
      },
    };
    return this.apiService
      .get(`${environment.api.kepler.docebo.url}/service/enrollments`, options)
      .then((response: any) => {
        let enrollment: Enrollment[] = [];
        if (response && response.length > 0) {
          enrollment = response.map((item: any) => {
            return this.enrollmentAdapter.adapt(item);
          });
        }
        this.enrollments = enrollment;

        return Promise.resolve(enrollment);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  /**
   * Enroll session for a course
   * @param userId pass the userId
   * @param courseId pass the courseId
   * @param sessionId - optinal. pass the sessionId
   * @returns enrolled session object if response is successful
   */
  enrollSession(userId: number, courseId: number, sessionId?: number): Promise<any> {
    if (!userId) {
      return Promise.reject('No userId provided');
    }

    return this.apiService
      .get(`${environment.api.kepler.docebo.url}/enrollments/${courseId}/${userId}/${sessionId}`)
      .then((response: any) => {
        if (response) {
          return Promise.resolve(response);
        } else {
          return Promise.reject(new Error('Unable to load Enrollment for a session'));
        }
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  /**
   * UnEnroll session for a course
   * @param userId pass the userId
   * @param courseId pass the courseId
   * @param sessionId - optinal. pass the sessionId
   * @returns unenrolled session object if response is successful
   */
  unEnrollSession(userId: number, courseId: number, sessionId: number): Promise<any> {
    if (!userId) {
      return Promise.reject('No userId provided');
    }

    return this.apiService
      .delete(
        `${environment.api.kepler.docebo.url}/enrollments/${courseId}/${userId}/session/${sessionId}`
      )
      .then((response: any) => {
        if (response.data.success === true) {
          return Promise.resolve(response);
        } else {
          return Promise.reject(
            new Error(
              `Unable to unenroll for a session ${sessionId} for the user ${userId}, course ${courseId}`,
              { cause: response }
            )
          );
        }
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  /**
   * Enroll session for a course
   * @param userId pass the userId
   * @param courseId pass the courseId
   * @param sessionId pass the sessionId
   * @returns enrolled session object if response is successful
   */
  enrollCourse(userId: number, courseId: number): Promise<any> {
    if (!userId) {
      return Promise.reject('No userId provided');
    }

    return this.apiService
      .get(`${environment.api.kepler.docebo.url}/enrollments/${courseId}/${userId}`)
      .then((response: any) => {
        if (response) {
          return Promise.resolve(response);
        } else {
          return Promise.reject(new Error('Unable to load Enrollment for a session'));
        }
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  /**
   *
   * @param courseId - pass the courseId
   * @param userId - pass the userId
   * @returns the Enrollemnt details of a user enrolled
   */
  public getEnrollmentDetails(courseId: number, userId: number): Promise<any> {
    return this.apiService
      .get(`${environment.api.kepler.docebo.url}/enrollments/${courseId}/${userId}`)
      .then((response: any) => {
        return Promise.resolve(response);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  /**
   * Create Certification Voucher for a user.
   * @param userData - current user details.
   * @param enrolledData - enrolled course details.
   * @returns the response of voucher details from rogueone service.
   */
  async createCertificationVoucherForUser(
    userData: User,
    courseId: number,
    courseName: string
  ): Promise<any> {
    const options = {
      email: userData?.email,
      courseId: courseId,
      courseName: courseName,
    };

    console.log('Options', options);

    return firstValueFrom(
      this.http.post(`${environment.voucherApi.url}/certification-voucher`, JSON.stringify(options))
    )
      .then((response: any) => {
        console.log('voucher certification', response);
        return Promise.resolve(response);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  /**
   * Re-enroll a user in all expired or archived courses that belong to a given subscription.
   * Called after a voucher is successfully redeemed to restore access to previously locked courses.
   * @param userId - Docebo user id
   * @param subscriptionCourseIds - Course ids belonging to the newly activated subscription
   * @returns Promise that resolves when all re-enrollments have been attempted
   */
  public async reEnrollExpiredSubscriptionCourses(
    userId: number,
    subscriptionCourseIds: number[]
  ): Promise<void> {
    if (!userId || !subscriptionCourseIds?.length) {
      return Promise.resolve();
    }

    // Statuses Docebo uses when a subscription expires and locks a course
    const EXPIRED_STATUSES = ['expired', 'overdue', 'archived'];

    let enrollments: Enrollment[];
    try {
      enrollments = await this.getEnrollments(userId);
    } catch (err) {
      console.error('reEnrollExpiredSubscriptionCourses: failed to fetch enrollments', err);
      return Promise.resolve();
    }

    const expiredEnrollments = enrollments.filter(
      (e) => e?.status && EXPIRED_STATUSES.includes(e.status.toLowerCase())
    );

    if (!expiredEnrollments.length) {
      return Promise.resolve();
    }

    const subscriptionCourseIdSet = new Set(subscriptionCourseIds);
    const coursesToReEnroll = expiredEnrollments.filter((e) => subscriptionCourseIdSet.has(e.id));

    if (!coursesToReEnroll.length) {
      return Promise.resolve();
    }

    // Re-enroll sequentially to avoid hammering the API
    for (const enrollment of coursesToReEnroll) {
      try {
        await this.enrollCourse(userId, enrollment.id);
        console.log(`reEnrollExpiredSubscriptionCourses: re-enrolled course ${enrollment.id}`);
      } catch (err) {
        // Log but don't throw — a single failure shouldn't block the others
        console.error(
          `reEnrollExpiredSubscriptionCourses: failed to re-enroll course ${enrollment.id}`,
          err
        );
      }
    }
  }
}
